import { Injectable } from '@angular/core';
import {
  JeevesProductListConfigService,
  ProductItemGroupFilterService,
  JeevesProductCardComponent,
  JeevesProductGridComponent
} from 'gung-standard-jeeves';
import {
  AuthService,
  ProductService,
  ProductArticleClassFilterService,
  Product,
  MetadataService,
  ProductSortService,
  LocationConfigService,
  GungFlowService,
  CloudPimFiltersService,
  CurrentAssortmentService
} from 'gung-standard';
import { ColmecJeevesProductTableComponent } from '../components/product/colmec-jeeves-product-table/colmec-jeeves-product-table.component';
import { ListLayoutMultipleComponent, ListLayout, SimpleConfigBaseFilter } from 'gung-list';
import { ColmecJeevesProductCardComponent } from '../components/product/colmec-jeeves-product-card/colmec-jeeves-product-card.component';
import { forkJoin, Observable, of } from 'rxjs';
import { filter, first, map, mergeMap, switchMap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { ColmecPimConceptsService } from './colmec-pim-concepts.service';

class ItemTypeFilterArItemtypecdOneAndTwoAndThree extends SimpleConfigBaseFilter<Product> {
  constructor(private metadataService: MetadataService) {
    super();
  }

  getName(): string {
    return 'DIMENSION';
  }

  getOptionIds(item: Product): string[] {
    let desc1 = '-';
    let desc2 = '-';
    let desc3 = '-';

    if (item.extra?.ar?.itemtypecd1) {
      desc1 = this.metadataService.getMetadataValue('atp1', 'itemtypedesc', item.extra.ar.itemtypecd1);
    }
    if (item.extra?.ar?.itemtypecd2) {
      desc2 = this.metadataService.getMetadataValue('atp2', 'itemtypedesc', item.extra.ar.itemtypecd2);
    }
    if (item.extra?.ar?.itemtypecd3) {
      desc3 = this.metadataService.getMetadataValue('atp3', 'itemtypedesc', item.extra.ar.itemtypecd3);
    }

    return [desc1 + '/' + desc2 + 'R' + desc3];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class ItemTypeFilterAr extends SimpleConfigBaseFilter<Product> {
  constructor(
    protected filterTitle: string,
    protected field: string,
    protected notFoundString: string,
    protected metadataService?: MetadataService, // Necessary to get Metadata
    protected tableName?: string, // Necessary to get Metadata
    protected valueName?: string // Necessary to get Metadata
  ) {
    super();
  }

  getName(): string {
    return this.filterTitle;
  }

  getOptionIds(item: Product): string[] {
    if (!item.extra?.ar?.hasOwnProperty(this.field)) {
      return [this.notFoundString];
    }
    return ['' + item.extra.ar[this.field]];
  }

  getOptionName(key: string): string {
    if (!this.tableName || !this.valueName) {
      return key;
    }
    return this.metadataService.getMetadataValue(this.tableName, this.valueName, key);
  }
}

class ItemTypeFilterArBoolean extends ItemTypeFilterAr {
  constructor(protected filterTitle: string, protected field: string, protected notFoundString: string) {
    super(filterTitle, field, notFoundString);
  }

  getOptionIds(item: Product): string[] {
    if (!item.extra?.ar?.hasOwnProperty(this.field)) {
      return [this.notFoundString];
    }
    if (this.filterTitle === 'AR_Q_ALIVE' && this.field === 'q_alive') {
      return [item.extra.ar[this.field] ? 'DISC_BRAKES' : 'DRUM_BRAKES'];
    }
    return [item.extra.ar[this.field] ? 'YES' : 'NO'];
  }
}

class PriceFilter extends SimpleConfigBaseFilter<Product> {
  constructor() {
    super();
    this.type = 'RangeConfigBaseFilter';
  }

  getName(): string {
    return 'PRICE';
  }

  getOptionIds(item: Product): string[] {
    if (!item.extra?.ar?.artlistpris) {
      return ['NO_PRICE'];
    }
    return ['' + item.extra.ar.artlistpris];
  }

  getOptionName(key: string): string {
    return key;
  }
}

class DiameterFilter extends SimpleConfigBaseFilter<Product> {
  constructor() {
    super();
  }

  getName(): string {
    return 'DIAMETER';
  }

  getOptionIds(item: Product): string[] {
    if (!item.extra?.ar?.itemtypecd3) {
      return ['NO_DIAMETER'];
    }
    return ['' + item.extra.ar.itemtypecd3];
  }

  getOptionName(key: string): string {
    return key;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ColmecProductListConfigService extends JeevesProductListConfigService {
  charReplaceRegex = /[.,-\/ R]/g;
  currentAssortment: string;
  topFilter = true;
  constructor(
    authService: AuthService,
    productService: ProductService,
    productItemGroupFilterService: ProductItemGroupFilterService,
    productArticleClassFilterService: ProductArticleClassFilterService,
    protected metadataService: MetadataService,
    productSortService: ProductSortService,
    protected locationConfigService: LocationConfigService,
    protected cloudPimFiltersService: CloudPimFiltersService,
    protected currentAssortmentService: CurrentAssortmentService,
    protected route: ActivatedRoute,
    protected gungFlowService: GungFlowService,
    protected colmecPimConceptsService: ColmecPimConceptsService
  ) {
    super(
      authService,
      productService,
      productItemGroupFilterService,
      productArticleClassFilterService,
      productSortService,
      gungFlowService,
      locationConfigService,
      cloudPimFiltersService,
      currentAssortmentService
    );
    this.assortmentSubject.subscribe(assortment => {
      this.currentAssortment = assortment;
    });
  }

  getItems(): Observable<Product[]> {
    return this.assortmentSubject.pipe(
      switchMap((assortment: string) => {
        if (assortment == null) {
          return forkJoin([
            of(assortment),
            this.authService.getCurrentUser().pipe(
              filter(user => !!user),
              first(),
              switchMap(user => this.fetchProducts(user && user.assortment))
            )
          ]);
        }
        return forkJoin([of(assortment), this.fetchProducts(assortment).pipe(first())]);
      }),
      mergeMap(([assortment, products]) => {
        return forkJoin([of(assortment), of(products), this.colmecPimConceptsService.getAllConcepts().pipe(first())]);
      }),
      switchMap(([assortment, products, concepts]) => {
        const productsWithConcept = products.filter(p => !!p.extra?.pim?.concept);
        const conceptIds = productsWithConcept.map(p => p.extra.pim.concept);
        const conceptsFiltered = concepts.filter(c => conceptIds.includes(c.id));
        const productsWithConceptIds = productsWithConcept.map(p => p.id);
        const productsFiltered = products.filter(p => !productsWithConceptIds.includes(p.id));
        conceptsFiltered.forEach(c => {
          const product = productsWithConcept.find(p => p.extra.pim.concept === c.id);
          if (product && (!c.images || c.images.length === 0)) {
            c.images = product.extra.images;
          }
          c.extra.ar = product.extra.ar;
          c.extra.products = productsWithConcept.filter(p => p.extra.pim.concept === c.id);
        });
        productsFiltered.push(...(conceptsFiltered as any));
        return of(productsFiltered);
      }),
      map(products =>
        products.sort((a, b) => {
          return a.name.localeCompare(b.name);
        })
      ),
      map(products =>
        products.sort((a, b) => {
          /*  // Sort by description (asc)
          return this.getDescription(a).localeCompare(this.getDescription(b));
        }).sort((a, b) => {
          // Sort by Pim attribute (desc)
          return this.getPimSortField(b) - this.getPimSortField(a);
        }).sort((a, b) => {
          // Sort by availability (desc) */
          return this.getCurrentAvailability(b) - this.getCurrentAvailability(a);
        })
      )
    );
  }

  protected getPimSortField(product: Product): number {
    if (product.extra.pim && product.extra.pim._colmecSortOrder) {
      return Number.parseInt(product.extra.pim._colmecSortOrder, 10) || 0;
    }

    return 0;
  }

  protected getCurrentAvailability(product: Product): number {
    if (product.extra.availabilities) {
      return Object.keys(product.extra.availabilities).map(key => {
        return product.extra.availabilities[key].currentAvailability || 0;
      })[0];
    }

    return 0;
  }

  protected getDescription(product: Product): string {
    return product.description || '';
  }

  getNothingFoundTranslateTag() {
    return 'COLMEC_NO_PRODUCTS_FOUND_CONTACT_US';
  }

  getSearchPlaceholderTranslate(): string {
    return 'COLMEC_SEARCH_PRODUCTS_PLACEHOLDER';
  }

  getSearchTerms(item: Product): string[] {
    const searchTerms: string[] = [];
    const name = item.name;
    searchTerms.push(name);
    const id = item.id;
    searchTerms.push(id);
    const desc4 = this.metadataService.getMetadataValue('atp4', 'itemtypedesc', item.extra?.ar?.itemtypecd4);
    searchTerms.push(desc4);

    let res = [];

    searchTerms.forEach(term => {
      if (this.charReplaceRegex.test(term)) {
        res = [...res, term, ...this.getReplacementTerms(term)];
      } else {
        res.push(term);
      }
    });

    return res;
  }

  protected getReplacementTerms(term: string): string[] {
    const res = [];
    res.push(term.replace(this.charReplaceRegex, ' '));
    res.push(term.replace(this.charReplaceRegex, '-'));
    res.push(term.replace(this.charReplaceRegex, ','));
    res.push(term.replace(this.charReplaceRegex, '.'));
    res.push(term.replace(this.charReplaceRegex, '/'));
    res.push(term.replace(this.charReplaceRegex, ''));
    res.push(term.replace(this.charReplaceRegex, 'R'));
    return res;
  }

  getBatchSizes() {
    return [12, 24, 48];
  }

  getLayouts(): ListLayout<Product>[] {
    const layoutTable = {
      getIconClass: () => 'fas fa-th-list',
      getListItemComponent: () => ColmecJeevesProductTableComponent,
      getListLayoutComponent: () => ListLayoutMultipleComponent,
      getName: () => 'ProductList'
    };

    const layoutGrid = {
      getIconClass: () => 'fas fa-th-large',
      getListItemComponent: () => ColmecJeevesProductCardComponent,
      getListLayoutComponent: () => ListLayoutMultipleComponent,
      getName: () => 'ProductGrid'
    };

    // for smaller devices the product table is not available
    if (window.innerWidth >= 576) {
      return [layoutGrid, layoutTable];
    }

    return [layoutGrid];
  }

  getFilters() {
    let filters = [];

    switch (this.currentAssortment) {
      case 's100':
        filters = [
          new ItemTypeFilterArItemtypecdOneAndTwoAndThree(this.metadataService),
          new ItemTypeFilterAr(
            'AR_VARUGRUPPKOD',
            'varugruppkod',
            'NO_BRAND_DEFINED',
            this.metadataService,
            'vg',
            'varugruppbeskr'
          ),
          new ItemTypeFilterAr(
            'AR_ITEMTYPECD5',
            'itemtypecd5',
            'NO_PATTERN_DEFINED',
            this.metadataService,
            'atp5',
            'itemtypedesc'
          ),
          new ItemTypeFilterAr(
            'AR_Q_KLASSID',
            'q_klassid',
            'NO_STOMKLASS',
            this.metadataService,
            'q_klass',
            'q_klassdesc'
          ),
          new ItemTypeFilterAr(
            'AR_Q_POSITION',
            'q_position',
            'NO_POSITION_DEFINED',
            this.metadataService,
            'q_position',
            'q_positiondesc'
          ),
          new ItemTypeFilterAr(
            'AR_ITEMTYPECD6',
            'itemtypecd6',
            'NO_USAGE_TYPE_DEFINED',
            this.metadataService,
            'atp6',
            'itemtypedesc'
          ),
          new ItemTypeFilterAr(
            'AR_Q_MONSTERDJUP',
            'q_monsterdjup',
            'NO_MONSTERDJUP_DEFINED',
            this.metadataService,
            'q_monsterdjup',
            'q_monsterdjupdesc'
          ),
          new ItemTypeFilterArBoolean('AR_Q_MS', 'q_ms', 'NO_MS_DEFINED'),
          new ItemTypeFilterArBoolean('AR_Q_3PMSF', 'q_3pmsf', 'NO_3PMSF_DEFINED'),
          new PriceFilter()
        ];
        break;
      case 's300':
        filters = [
          new ItemTypeFilterAr(
            'AR_Q_BULTCIRKEL',
            'q_bultcirkel',
            'UNDEFINED',
            this.metadataService,
            'q_bultcirkel',
            'q_bultcirkeldesc'
          ),
          new ItemTypeFilterAr(
            'AR_Q_NAVHAL',
            'q_navhal',
            'UNDEFINED',
            this.metadataService,
            'q_navhal',
            'q_navhaldesc'
          ),
          new ItemTypeFilterAr(
            'AR_Q_INPRESSNING',
            'q_inpressning',
            'UNDEFINED',
            this.metadataService,
            'q_inpressning',
            'q_inpressningdesc'
          ),
          new ItemTypeFilterAr(
            'AR_Q_BULTHAL',
            'q_bulthal',
            'UNDEFINED',
            this.metadataService,
            'q_bulthal',
            'q_bulthaldesc'
          ),
          new ItemTypeFilterArBoolean('AR_Q_ALIVE', 'q_alive', 'UNDEFINED'),
          new ItemTypeFilterAr(
            'AR_Q_MAXBELASTNING',
            'q_maxbelastning',
            'UNDEFINED',
            this.metadataService,
            'q_maxbelastning',
            'q_maxbelastningdesc'
          ),
          new PriceFilter(),
          new DiameterFilter()
        ];
        break;
      case 's400':
        filters = [new PriceFilter()];
        break;
      default:
        filters = [
          new ItemTypeFilterArItemtypecdOneAndTwoAndThree(this.metadataService),
          new ItemTypeFilterAr(
            'AR_VARUGRUPPKOD',
            'varugruppkod',
            'NO_BRAND_DEFINED',
            this.metadataService,
            'vg',
            'varugruppbeskr'
          ),
          new ItemTypeFilterAr(
            'AR_ITEMTYPECD5',
            'itemtypecd5',
            'NO_PATTERN_DEFINED',
            this.metadataService,
            'atp5',
            'itemtypedesc'
          ),
          new ItemTypeFilterAr(
            'AR_Q_POSITION',
            'q_position',
            'NO_POSITION_DEFINED',
            this.metadataService,
            'q_position',
            'q_positiondesc'
          ),
          new ItemTypeFilterAr(
            'AR_ITEMTYPECD6',
            'itemtypecd6',
            'NO_USAGE_TYPE_DEFINED',
            this.metadataService,
            'atp6',
            'itemtypedesc'
          ),
          new ItemTypeFilterAr(
            'AR_Q_MONSTERDJUP',
            'q_monsterdjup',
            'NO_MONSTERDJUP_DEFINED',
            this.metadataService,
            'q_monsterdjup',
            'q_monsterdjupdesc'
          ),
          new ItemTypeFilterArBoolean('AR_Q_MS', 'q_ms', 'NO_MS_DEFINED'),
          new ItemTypeFilterArBoolean('AR_Q_3PMSF', 'q_3pmsf', 'NO_3PMSF_DEFINED'),
          new PriceFilter()
        ];
        break;
    }
    return filters;
  }

  hideZeroOptions() {
    return true;
  }

  getAddQueryParameterLimitToUrl(): boolean {
    return true;
  }

  setPreviousLayout(index: number) {
    this.locationConfigService.addLayout({
      path: 'current-layout', // Save the current layout when click layout
      layoutIndex: index
    });
  }

  getPreviousLayout(): number {
    const defaultLayout = this.locationConfigService.getLayout('products'); // Get previous layout from products
    this.locationConfigService.removeLayout(defaultLayout);
    return Number((defaultLayout && defaultLayout.layoutIndex) || 0);
  }
}
